import {
  type MouseEvent,
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaFilterListRegular, FaFilterListSolid } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  type ToolbarStatusItem,
  ToolbarStatusItems,
} from '~/components/common/filters/ToolbarStatusItems'
import { pinAreaAndPeriodFirst } from '~/components/common/filters/filterItemOrder'
import { iconSize } from '~/constants/iconSize'
import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'

/** 開閉の保存キーの接頭辞。画面ごとに `storageKey` を足す。 */
const STORAGE_PREFIX = 'ebalance-filter-collapsible:'

/** Tab で止まる要素 (キーボードで開いた直後にフォーカスを移す先)。 */
const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** 値がこの幅を超えたら … で切り、全文は hover の説明 (title) に出す (折り返して行を増やさない)。 */
const SUMMARY_VALUE_MAX_WIDTH = '240px'
/** これより長い文字の値だけ切り詰めの器に入れる。 */
const SUMMARY_TRUNCATE_MIN_LENGTH = 14

/** 閉じたバーの 1 行目の部品の高さ (TertiaryButton sm)。 */
const CONTROL_HEIGHT_PX = 32

/**
 * Enter / Space で押されたか (キーボードで押したボタンの click は `detail` が 0)。
 * 開いた直後にフォーカスを中へ移すのはキーボードの時だけ。マウスで開いた人は
 * 同じボタンをもう一度押して閉じられるよう、フォーカスをボタンに残す。
 */
const isKeyboardActivation = (event: MouseEvent): boolean => event.detail === 0

const toTruncatedItem = (item: ToolbarStatusItem): ToolbarStatusItem =>
  typeof item.value === 'string' &&
  item.value.length >= SUMMARY_TRUNCATE_MIN_LENGTH
    ? {
        ...item,
        description: item.description ?? `${item.label} ${item.value}`,
        value: (
          <Text
            as="span"
            textStyle="caption"
            display="inline-block"
            verticalAlign="bottom"
            overflow="hidden"
            maxWidth={SUMMARY_VALUE_MAX_WIDTH}
            textOverflow="ellipsis"
          >
            {item.value}
          </Text>
        ),
      }
    : item

/** 画面ごとの開閉の保存。未保存・不正値は閉 (既定)。 */
const useExpandedState = (
  storageKey: string,
): [boolean, (next: boolean) => void] => {
  const key = `${STORAGE_PREFIX}${storageKey}`
  const [isExpanded, setExpanded] = useState(
    () => getLocalStorageItem(key) === 'open',
  )
  const update = (next: boolean): void => {
    setExpanded(next)
    setLocalStorageItem(key, next ? 'open' : 'closed')
  }
  return [isExpanded, update]
}

/** 要素の寸法を ResizeObserver で追う (jsdom など未対応環境では初回の値だけ)。 */
const useMeasuredSize = (
  dimension: 'offsetWidth' | 'offsetHeight',
  isPresent: boolean,
): [React.RefObject<HTMLDivElement>, number] => {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState(0)
  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) {
      setSize(0)
      return undefined
    }
    const update = (): void => {
      const next = element[dimension]
      setSize((current) => (current === next ? current : next))
    }
    update()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [dimension, isPresent])
  return [ref, size]
}

type Props = {
  /** 開閉を覚えるキー (画面ごとに一意。例: `'intradayMarket.bidList'`)。 */
  storageKey: string
  /**
   * 既定から変えている条件の数 (既定 = 「クリア」で戻る値)。閉じている間も絞り込まれていることが
   * 分かるよう、ボタンの中・アイコンの右に本文で出す (括弧・バッジなし)。0 なら出さない。
   */
  modifiedCount: number
  /**
   * 閉じていても操作する部品 (表示日の入力など)。フィルターボタンの直後・要約の前。
   * 要約が折り返してもボタンの隣に残る。40px の部品を置くと 1 行目が 40px になり、閉じた行は 58px になる。
   */
  leading?: ReactNode
  /** 今の条件の読み取り専用の要約 (12px)。エリア → 期間関連 → 残りの順に並べ直す。 */
  summary: readonly ToolbarStatusItem[]
  /** 要約の後ろに続く状況 (14px。件数・最新時刻・状態のピル)。Tab で止まらない読み取り専用のものだけ。 */
  status?: ReactNode
  /** 変えている条件をまとめて既定に戻す。省略した画面は「クリア」を押せない状態で出す。 */
  onClear?: () => void
  /** 右端・「クリア」の左に常に出す部品 (GC までのカウントダウンなど)。 */
  trailing?: ReactNode
  /**
   * 要約を渡した順のまま並べる (既定は [[pinAreaAndPeriodFirst]] でエリア → 期間関連 → 残り)。
   * 発注主体を先頭にしたい画面のように、 カードの並びを画面が決める時に [[FilterGrid]] の `keepOrder` と対で使う。
   */
  keepSummaryOrder?: boolean
  /**
   * 枠 (線・角丸・白地) を付けるか。 既定は付ける。 枠のあるアコーディオンの中など、 外側の器が
   * 枠を持つ場所に置く時だけ false にして、 カードの中にカードを入れ子にしない
   * (UI 辞書 toolbar-collapsible.md「下段に枠を付けない」と同じ理由)。 区切り線は呼び出し側で引く。
   */
  framed?: boolean
  /** 開いた条件のカードの中身。通常は [[FilterGrid]]。部品は 40px (md) のまま置く。 */
  children: ReactNode
}

/**
 * 開閉式フィルター (UI 辞書 toolbar-collapsible.md「開閉式」)。
 *
 * 1 枚のカードに、上段 = ツールバーの行、下段 = 条件のカード (開いた時だけ・間は 1px の区切り線)。
 * 開閉は画面ごとに localStorage へ保存し、既定は閉。ツールバーの行は開閉で形を変えない。
 *
 * - 行の余白は上下 8px・左右 12px。閉じた行は 50px (32px の部品 + 余白 16px + 枠 2px)。
 *   `leading` に 40px の部品を置く画面は 58px
 * - 左端のフィルターボタン = Tertiary sm (32px)。閉 = Regular / 開 = Solid のアイコン。
 *   件数 0 は 32×32、件数ありはアイコンの右に件数を本文で出す (高さは 32px のまま)
 * - 幅が足りない時は要約・状況だけ 2 行目へ回す (行の間 8px)。ボタン・leading・trailing・クリアは 1 行目
 * - 右端 (trailing → クリア) は 1 行目の高さの中で上下中央。クリアは変えた条件が無い時、
 *   理由を hover / focus の Tooltip で出して押せない状態にする
 * - Esc: 開いたカードの中で押すと閉じてボタンへフォーカスを戻す (中の部品が Esc を先に使った時は閉じない)
 * - Tab: ボタン → leading → クリア。要約・状況は止まらない。閉じている間はカードごと `hidden`
 */
export const CollapsibleFilterToolbar = ({
  storageKey,
  modifiedCount,
  leading,
  summary,
  status,
  onClear,
  trailing,
  keepSummaryOrder = false,
  framed = true,
  children,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isExpanded, setExpanded] = useExpandedState(storageKey)
  const panelId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const tooltip = isExpanded
    ? t('common:filter.iconToggleClose')
    : t('common:filter.iconToggleOpen')
  const modifiedMessage = t('common:filter.iconToggleModified', {
    count: modifiedCount,
  })
  const isClearDisabled = onClear === undefined || modifiedCount === 0

  // 右端 (trailing + クリア) は絶対配置。要約が右端に重ならないよう、その幅だけ行の右を空ける
  const [rightRef, rightWidth] = useMeasuredSize('offsetWidth', true)
  // 1 行目の高さ = leading (40px の入力欄など) とボタン (32px) の高い方
  const hasLeading = leading !== undefined
  const [leadingRef, leadingHeight] = useMeasuredSize(
    'offsetHeight',
    hasLeading,
  )
  const firstRowHeight = Math.max(CONTROL_HEIGHT_PX, leadingHeight)
  const hasSummary = summary.length > 0 || status !== undefined

  const toggle = (event: MouseEvent): void => {
    const next = !isExpanded
    setExpanded(next)
    if (next && isKeyboardActivation(event)) {
      requestAnimationFrame(() => {
        panelRef.current
          ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
          ?.focus()
      })
    }
  }

  return (
    <Box
      position="relative"
      {...(framed
        ? {
            borderWidth: '1px',
            borderColor: 'Border.lowEmphasis',
            borderRadius: 'lg',
            backgroundColor: 'Surface.primary',
          }
        : {})}
    >
      <Flex
        alignItems="center"
        flexWrap="wrap"
        rowGap="8px"
        columnGap="20px"
        paddingX={3}
        paddingY={2}
        style={
          rightWidth > 0 ? { paddingRight: `${rightWidth + 32}px` } : undefined
        }
      >
        <Tooltip
          content={
            modifiedCount > 0 ? `${tooltip} (${modifiedMessage})` : tooltip
          }
        >
          <TertiaryButton
            ref={buttonRef}
            flexShrink={0}
            minWidth="32px"
            aria-controls={panelId}
            aria-describedby={modifiedCount > 0 ? descriptionId : undefined}
            aria-expanded={isExpanded}
            aria-label={t('common:filter.iconToggle')}
            onClick={toggle}
            paddingX={modifiedCount > 0 ? 2 : 0}
            size="sm"
          >
            <Flex alignItems="center" gap={1}>
              {/* 閉 = 線 (Regular) / 開 = 塗り (Solid) */}
              {isExpanded ? (
                <FaFilterListSolid
                  boxSize={iconSize.labeledButton}
                  aria-hidden
                />
              ) : (
                <FaFilterListRegular
                  boxSize={iconSize.labeledButton}
                  aria-hidden
                />
              )}
              {modifiedCount > 0 && (
                <Text as="span" textStyle="body" aria-hidden>
                  {modifiedCount}
                </Text>
              )}
            </Flex>
          </TertiaryButton>
        </Tooltip>
        {/* 読み上げ用の説明 (hidden でも aria-describedby からは読める) */}
        <span hidden id={descriptionId}>
          {modifiedMessage}
        </span>
        {hasLeading && (
          <Box
            ref={leadingRef}
            alignItems="center"
            flexShrink={0}
            display="inline-flex"
          >
            {leading}
          </Box>
        )}
        {/* 要約・状況。1 行目に入らない時は器ごと 2 行目へ回す */}
        {hasSummary && (
          <Flex
            alignItems="center"
            flexWrap="wrap"
            flex="1 1 240px"
            gap="8px 20px"
            minWidth="240px"
            data-testid="collapsible-filter-summary"
          >
            <ToolbarStatusItems
              textStyle="caption"
              flowInParent
              items={(keepSummaryOrder
                ? summary
                : pinAreaAndPeriodFirst(summary)
              ).map(toTruncatedItem)}
            />
            {status}
          </Flex>
        )}
      </Flex>
      {/* trailing とクリアは 1 行目の右に固定する (要約が回っても動かない)。1 行目の高さの中で上下中央 */}
      <Flex
        ref={rightRef}
        position="absolute"
        top="8px"
        right="12px"
        alignItems="center"
        gap="20px"
        height={`${firstRowHeight}px`}
      >
        {trailing}
        <ButtonTooltip
          content={
            isClearDisabled ? t('common:filter.clearDisabled') : undefined
          }
        >
          <TertiaryButton
            flexShrink={0}
            disabled={isClearDisabled}
            onClick={onClear}
            size="sm"
          >
            {t('common:action.clear')}
          </TertiaryButton>
        </ButtonTooltip>
      </Flex>
      {/* 同じカードの下段。開いている時だけ区切り線 1px。Esc は中の部品が先に使った時 (defaultPrevented) は閉じない。
          閉じている間は中身を描かない (hidden だけだと、中の select・複数選択が document.body に出した候補の
          メニューが閉じた後も残り、操作できてしまう)。aria-controls の参照先として外枠は残す */}
      <Box
        ref={panelRef}
        borderColor="Border.lowEmphasis"
        borderTopWidth="1px"
        hidden={!isExpanded}
        id={panelId}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || event.defaultPrevented) return
          setExpanded(false)
          buttonRef.current?.focus()
        }}
      >
        {isExpanded ? children : null}
      </Box>
    </Box>
  )
}
